/**
 * <film-strip> — arrows for the mobile film-strip carousel.
 *
 * A REAL ASSET, NOT A SECTION `{% javascript %}` BLOCK. A section's inline JS was
 * measured NOT executing at all on this store, which cost three rounds of
 * debugging on the header. Everything interactive in this theme is a file in
 * assets/ loaded by an explicit <script defer> in layout/theme.liquid, where the
 * load order is ours to see and a failure shows up in the network panel.
 *
 * THIS IS THE SECOND CONSUMER OF THIS EXACT CAROUSEL LOGIC — <product-gallery> in
 * assets/product-main.js is the first, and this is a deliberate copy of it rather
 * than a shared base. Copying literals is the theme's recorded precedent, and two
 * consumers is a coincidence. A THIRD would justify extracting one; there is a
 * matching note in product-main.js.
 *
 * Nothing here runs above `lg`: the arrows are `lg:hidden`, so they cannot be
 * clicked, and the track is not a scroll container at that width, so its
 * scrollLeft never moves. No breakpoint guard is needed and none is written — a
 * matchMedia listener here would be state that can go stale for no gain.
 *
 * Touches no shared state, so its position among the deferred scripts does not
 * matter. It does not read window.themeScroll on upgrade — only inside a handler,
 * by which time scroll.js has long since run — so it needs none of the
 * `themescroll:ready` handshake other components carry.
 */

if (!customElements.get("film-strip")) {
  customElements.define(
    "film-strip",
    class FilmStrip extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector("[data-strip]");
        this.prevButton = this.querySelector("[data-strip-prev]");
        this.nextButton = this.querySelector("[data-strip-next]");
        this.statusRegion = this.querySelector("[data-strip-status]");

        if (!this.track || !this.prevButton || !this.nextButton) return;

        this.slides = Array.from(this.track.querySelectorAll("[data-slide]"));
        if (this.slides.length < 2) return;

        this.handlePrev = this.handlePrev.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleScroll = this.handleScroll.bind(this);

        this.prevButton.addEventListener("click", this.handlePrev);
        this.nextButton.addEventListener("click", this.handleNext);
        this.track.addEventListener("scroll", this.handleScroll, { passive: true });

        // Revealed only now, so a visitor never meets a control that does nothing
        // because this file failed to load.
        this.prevButton.hidden = false;
        this.nextButton.hidden = false;
        this.syncButtons();
      }

      disconnectedCallback() {
        this.prevButton?.removeEventListener("click", this.handlePrev);
        this.nextButton?.removeEventListener("click", this.handleNext);
        this.track?.removeEventListener("scroll", this.handleScroll);
      }

      /**
       * Derived from scroll position, never stored. A stored index goes stale the
       * moment someone swipes, and there is no event that reliably tells us a
       * momentum scroll has settled.
       */
      get currentIndex() {
        const width = this.slides[0].offsetWidth;
        if (!width) return 0;
        return Math.round(this.track.scrollLeft / width);
      }

      handlePrev() {
        this.scrollToIndex(this.currentIndex - 1);
      }

      handleNext() {
        this.scrollToIndex(this.currentIndex + 1);
      }

      handleScroll() {
        this.syncButtons();
      }

      scrollToIndex(index) {
        const target = Math.min(Math.max(index, 0), this.slides.length - 1);
        const slide = this.slides[target];
        if (!slide) return;

        // Prefer the shared instance's answer so the whole site agrees, but fall
        // back — this can run before scroll.js on a slow connection.
        const reduced = window.themeScroll
          ? window.themeScroll.prefersReducedMotion
          : window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.track.scrollTo({
          left: slide.offsetLeft - this.track.offsetLeft,
          behavior: reduced ? "auto" : "smooth",
        });

        this.announce(target);
      }

      /**
       * Disabled, not hidden. A control that disappears at the end of the track
       * moves the one beside it and takes the focus ring with it.
       */
      syncButtons() {
        const index = this.currentIndex;
        this.prevButton.disabled = index <= 0;
        this.nextButton.disabled = index >= this.slides.length - 1;
      }

      /**
       * Arrow presses only. A swipe fires dozens of scroll events and would flood
       * a screen reader. The string comes from a Liquid-rendered <template>, so
       * the translation is never assembled here.
       */
      announce(index) {
        if (!this.statusRegion) return;
        const template = this.querySelector("[data-strip-status-template]");
        if (!template) return;

        this.statusRegion.textContent = template.content.textContent
          .replace("__current__", String(index + 1))
          .replace("__total__", String(this.slides.length));
      }
    },
  );
}
